import React, { useContext, useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Input,
  Label,
  FormGroup,
  Table,
  Badge,
} from "reactstrap";
import * as XLSX from "xlsx";

import WhatsappService from "../../service/whatsapp.service";
import { ApiContext } from "../../contexts/ApiContext";
import { useNotification } from "../../contexts/NotificationContext";

const STATUS = {
  accepted: { label: "Aceita pela Meta", color: "secondary" },
  sent: { label: "Enviada", color: "info" },
  delivered: { label: "Entregue", color: "primary" },
  read: { label: "Lida", color: "success" },
  failed: { label: "Falhou", color: "danger" },
};

const ORIGINS = {
  order_status: "Status do pedido",
  bot: "Miranda Bot",
  test: "Teste",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "");

const localDate = (offsetDays = 0) =>
  new Date(Date.now() - offsetDays * 24 * 60 * 60 * 1000).toLocaleDateString("sv-SE");

const emptyFilters = () => ({ from: localDate(7), to: localDate(), status: "", origin: "", search: "" });

const emptyResult = { data: [], total: 0, totalPages: 1, summary: null };

const PAGE_SIZE = 30;
const EXPORT_PAGE_SIZE = 100;

const pct = (value, total) => (total ? `${Math.round((value / total) * 100)}%` : "—");

const errorTitle = (m) =>
  [m.errorCode != null ? `Código ${m.errorCode}` : null, m.errorDetail].filter(Boolean).join(" · ");

const WhatsappMessagesReportPage = () => {
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  const accountId = account?.id;

  const [filters, setFilters] = useState(emptyFilters);
  const [result, setResult] = useState(emptyResult);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = (f, overrides = {}) => ({
    from: f.from || undefined,
    to: f.to || undefined,
    status: f.status || undefined,
    origin: f.origin || undefined,
    search: f.search.trim() || undefined,
    ...overrides,
  });

  const load = async (p = 1, f = filters) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await WhatsappService.getMessagesReport(accountId, buildParams(f, { page: p, size: PAGE_SIZE }));
      setResult(res || emptyResult);
      setPage(res?.page || p);
    } catch (err) {
      console.error("Falha ao carregar mensagens enviadas", err);
      notify("tr", "danger", "Erro ao carregar mensagens enviadas", "icon-alert-circle-exc");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountId) load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const onFilterChange = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClear = () => {
    const f = emptyFilters();
    setFilters(f);
    load(1, f);
  };

  const handleExport = async () => {
    if (!accountId) return;
    setExporting(true);
    try {
      const rows = [];
      let p = 1;
      let totalPages = 1;
      do {
        const res = await WhatsappService.getMessagesReport(
          accountId,
          buildParams(filters, { page: p, size: EXPORT_PAGE_SIZE }),
        );
        rows.push(...(res?.data || []));
        totalPages = res?.totalPages || 1;
        p += 1;
      } while (p <= totalPages);

      const sheet = rows.map((m) => ({
        Data: fmtDate(m.createdAt),
        Destino: m.to,
        Origem: ORIGINS[m.origin] || m.origin,
        Pedido: m.orderId || "",
        Tipo: m.kind === "template" ? `Template ${m.templateName || ""}`.trim() : m.kind,
        Conteúdo: m.body || "",
        Status: STATUS[m.status]?.label || m.status,
        Entregue: fmtDate(m.deliveredAt),
        Lida: fmtDate(m.readAt),
        Erro: m.errorMessage || "",
        "Código do erro": m.errorCode ?? "",
        "Cobrança Meta": m.billable == null ? "" : m.billable ? `Sim (${m.pricingCategory || "—"})` : "Não",
      }));
      const ws = XLSX.utils.json_to_sheet(sheet);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Mensagens");
      XLSX.writeFile(wb, `mensagens-whatsapp-${localDate()}.xlsx`);
    } catch (err) {
      console.error("Falha ao exportar", err);
      notify("tr", "danger", "Erro ao exportar mensagens", "icon-alert-circle-exc");
    } finally {
      setExporting(false);
    }
  };

  const s = result.summary || { total: 0, delivered: 0, read: 0, failed: 0 };
  const delivered = (s.delivered || 0) + (s.read || 0);
  const cards = [
    { label: "Enviadas", value: s.total, hint: "no período", color: "info", icon: "icon-send" },
    { label: "Entregues", value: delivered, hint: pct(delivered, s.total), color: "primary", icon: "icon-check-2" },
    { label: "Lidas", value: s.read || 0, hint: pct(s.read || 0, s.total), color: "success", icon: "icon-single-02" },
    { label: "Falhas", value: s.failed || 0, hint: pct(s.failed || 0, s.total), color: "danger", icon: "icon-alert-circle-exc" },
  ];

  return (
    <>
      <Row>
        {cards.map((c) => (
          <Col lg="3" sm="6" key={c.label}>
            <Card className="card-stats">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="card-category mb-1">{c.label}</p>
                    <CardTitle tag="h3" className="mb-0">
                      {loading && !result.summary ? "…" : c.value.toLocaleString("pt-BR")}
                    </CardTitle>
                    <small className="text-muted">{c.hint}</small>
                  </div>
                  <i className={`tim-icons ${c.icon} text-${c.color}`} style={{ fontSize: 28 }} />
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        <Col xs="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Mensagens enviadas pelo WhatsApp</CardTitle>
              <p className="card-category mb-0" style={{ textTransform: "none", letterSpacing: "normal" }}>
                Avisos de status do pedido, respostas do Miranda Bot e testes. Na Cloud API o status é atualizado pela Meta
                (entregue, lida ou falha); no QR Code o registro fica em enviada ou falhou.
              </p>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>De</Label>
                    <Input type="date" value={filters.from} onChange={onFilterChange("from")} />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Até</Label>
                    <Input type="date" value={filters.to} onChange={onFilterChange("to")} />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Status</Label>
                    <Input type="select" value={filters.status} onChange={onFilterChange("status")}>
                      <option value="">Todos</option>
                      {Object.entries(STATUS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Origem</Label>
                    <Input type="select" value={filters.origin} onChange={onFilterChange("origin")}>
                      <option value="">Todas</option>
                      {Object.entries(ORIGINS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Telefone</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Buscar..."
                      value={filters.search}
                      onChange={onFilterChange("search")}
                      onKeyDown={(e) => e.key === "Enter" && load(1)}
                    />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6" className="d-flex align-items-end">
                  <FormGroup className="w-100 d-flex" style={{ gap: 8 }}>
                    <Button color="primary" className="m-0" onClick={() => load(1)} disabled={loading}>
                      Filtrar
                    </Button>
                    <Button color="secondary" className="m-0" onClick={handleClear} disabled={loading}>
                      Limpar
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col sm="6">
                  <CardTitle tag="h4">{result.total} mensagem(ns)</CardTitle>
                </Col>
                <Col sm="6" className="text-right">
                  <Button color="success" onClick={handleExport} disabled={exporting || !result.total}>
                    <i className="tim-icons icon-cloud-download-93 mr-1" />
                    {exporting ? "Exportando..." : "Exportar XLSX"}
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Table className="tablesorter" responsive>
                <thead className="text-primary">
                  <tr>
                    <th>Data</th>
                    <th>Destino</th>
                    <th>Origem</th>
                    <th>Pedido</th>
                    <th>Conteúdo</th>
                    <th>Status</th>
                    <th>Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        Carregando...
                      </td>
                    </tr>
                  ) : result.data.length ? (
                    result.data.map((m) => (
                      <tr key={m.id}>
                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(m.createdAt)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{m.to}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{ORIGINS[m.origin] || m.origin}</td>
                        <td>{m.orderId ? `#${m.orderId}` : "—"}</td>
                        <td style={{ maxWidth: 320 }}>
                          <div
                            title={m.body || ""}
                            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {m.body || "—"}
                          </div>
                          {m.kind === "template" && m.templateName && (
                            <small className="text-muted">Template {m.templateName}</small>
                          )}
                        </td>
                        <td>
                          <Badge color={STATUS[m.status]?.color || "secondary"} pill>
                            {STATUS[m.status]?.label || m.status}
                          </Badge>
                        </td>
                        <td style={{ maxWidth: 280 }}>
                          {m.errorMessage ? (
                            <span className="text-danger" title={errorTitle(m)} style={{ cursor: "help" }}>
                              {m.errorMessage}
                              {m.errorCode != null && <small className="text-muted ml-1">({m.errorCode})</small>}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        Nenhuma mensagem encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {result.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button color="info" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
                    Anterior
                  </Button>
                  <span className="text-muted">
                    Página {page} de {result.totalPages}
                  </span>
                  <Button
                    color="info"
                    size="sm"
                    disabled={page >= result.totalPages || loading}
                    onClick={() => load(page + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default WhatsappMessagesReportPage;
